import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Hammer, Info } from 'lucide-react';
import { useMaterials, type DatabaseMaterial } from '@/hooks/useMaterials';
import { useHiddenMaterials } from '@/hooks/useHiddenMaterials';
import { formatNumber, formatEuro } from '@/utils/format';
import {
  ANCHOR_SPACING_MM,
  anchorPricePerPiece,
  anchorQuantityPerSqm,
  listUsableAnchors,
} from '../hooks/anchors';
import type { LayerV2 } from '../types';

interface LayerAnchorSelectorProps {
  layer: LayerV2;
  /** ml di guida per m² di parete, da cui si deriva il numero di tasselli. */
  guideMlPerSqm: number;
  onChangeAnchor: (anchor: DatabaseMaterial | null) => void;
}

const NONE = '__none__';

/**
 * Selettore del fissaggio con cui la guida e' ancorata a pavimento e a soffitto.
 *
 * Un'unica lista per TASSELLI e CHIODI a sparo: sono modi alternativi di fare
 * la stessa cosa (forare e tassellare, oppure sparare il chiodo con
 * l'inchiodatrice) e si contano allo stesso modo.
 *
 * La QUANTITA' non e' modificabile: discende dal passo di fissaggio (50 cm) e
 * dai metri lineari di guida. Per metro lineare di parete sono sempre 4, a
 * qualunque altezza — mostrarlo come campo editabile darebbe l'impressione
 * sbagliata che sia una stima da ritoccare.
 *
 * L'elenco contiene SOLO i prodotti con un prezzo al pezzo ricavabile. In
 * catalogo 30 tasselli Knauf sono venduti a scatola senza dichiarare quanti
 * pezzi contiene: proporli significherebbe leggere 10 € di scatola come 10 €
 * di pezzo, cioe' ~15 €/m² di soli fissaggi (vedi `anchors.ts`).
 */
const LayerAnchorSelector: React.FC<LayerAnchorSelectorProps> = ({
  layer,
  guideMlPerSqm,
  onChangeAnchor,
}) => {
  const { data: catalog = [] } = useMaterials();
  const { hiddenSet } = useHiddenMaterials();

  const options = useMemo(
    () => listUsableAnchors(catalog, hiddenSet),
    [catalog, hiddenSet],
  );

  const current = layer.anchorMaterial ?? null;
  const qty = anchorQuantityPerSqm(guideMlPerSqm);
  const pricePerPiece = anchorPricePerPiece(current);
  const costPerSqm = pricePerPiece != null ? pricePerPiece * qty : 0;

  return (
    <div className="mt-2 px-3 py-2 rounded border border-zinc-200 bg-white/60">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <Hammer className="h-3.5 w-3.5 shrink-0 text-zinc-600" />

        <Select
          value={current?.id ?? NONE}
          onValueChange={(v) => {
            onChangeAnchor(v === NONE ? null : (options.find(o => o.id === v) ?? null));
          }}
        >
          <SelectTrigger className="h-7 text-[11px] flex-1 min-w-0">
            {/* Children espliciti: il rendering di default clonerebbe le due
                righe dell'item, che nel campo chiuso non ci stanno. */}
            <SelectValue placeholder="Nessun fissaggio">
              {current
                ? <span className="truncate">
                    <span className="text-muted-foreground">{current.supplier}</span>
                    {' · '}{current.name}
                  </span>
                : 'Nessun fissaggio'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE} className="text-[11px]">Nessun fissaggio</SelectItem>
            {options.map(o => (
              <SelectItem key={o.id} value={o.id} className="text-[11px]">
                <div className="leading-tight">
                  <div>{o.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {o.supplier} · {formatNumber(anchorPricePerPiece(o) ?? 0, 4)} €/pz
                    {o.unit === 'scatola' && o.box_pieces
                      ? ` · conf. ${formatNumber(o.box_pieces, 0)} pz`
                      : ''}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {current && (
          <>
            <Badge variant="outline" className="text-[9px] font-mono px-1 py-0 shrink-0">
              {formatNumber(qty)} pz/m²
            </Badge>
            <span className="font-medium text-zinc-800 shrink-0">
              {formatEuro(costPerSqm)}/m²
            </span>
          </>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="shrink-0 text-zinc-400 hover:text-zinc-600">
                <Info className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              <p className="font-medium mb-1">Fissaggio della guida</p>
              <p>
                Un fissaggio ogni {ANCHOR_SPACING_MM / 10} cm, a pavimento e a soffitto:
                {' '}{formatNumber(1000 / ANCHOR_SPACING_MM, 0)} pz per metro lineare di guida.
              </p>
              <p className="mt-1">
                Con {formatNumber(guideMlPerSqm)} ml di guida al m² fanno{' '}
                <strong>{formatNumber(qty)} pz/m²</strong>. Per metro lineare di parete sono
                sempre {formatNumber((1000 / ANCHOR_SPACING_MM) * 2, 0)}, a qualunque altezza.
              </p>
              <p className="mt-1 text-muted-foreground">
                Tassello e chiodo a sparo sono alternativi: si contano allo stesso modo.
                L'elenco mostra solo i prodotti con un prezzo al pezzo ricavabile — quelli
                venduti a confezione senza il numero di pezzi darebbero un costo falsato.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default LayerAnchorSelector;
